import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const TextLesson = ({ task }) => {
  const [isExpanded, setIsExpanded] = useState(!task.lesson_collapsed);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    if (selectedImage) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const hasLesson = task.lesson_content && task.lesson_content.trim() && task.lesson_content !== '<p><br></p>';

  if (!hasLesson) return null;

  const handleContentClick = (e) => {
    // If the clicked element is an image, open it in the lightbox
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      setSelectedImage(e.target.src);
    }
  };

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-2xl overflow-hidden mb-6 shadow-sm">
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-100">
              <SafeIcon name="Book" className="w-5 h-5" />
            </div>
            <div>
              <span className="font-black text-blue-900 block leading-none">
                {task.lesson_title || 'Training Lesson'}
              </span>
              <span className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">
                Module Content
              </span>
            </div>
          </div>
          <SafeIcon name={isExpanded ? "ChevronUp" : "ChevronDown"} className="w-5 h-5 text-blue-600" />
        </button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6 border-t border-blue-200 bg-white">
                <div 
                  className="prose prose-indigo max-w-none text-gray-800 mt-5 [&_img]:max-w-full [&_img]:h-auto"
                  dangerouslySetInnerHTML={{ __html: task.lesson_content }}
                  onClick={handleContentClick}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Premium Lightbox for Images */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-[10005] flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setSelectedImage(null)}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-8 right-8 text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-[10006]"
            >
              <SafeIcon name="X" className="w-8 h-8" />
            </button>

            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative max-w-7xl flex flex-col items-center"
              onClick={e => e.stopPropagation()} // Prevents clicking the image itself from closing
            >
              <img 
                src={selectedImage} 
                alt="Lesson Detail" 
                className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/10 cursor-default"
              />
              
              <div className="mt-6 text-white/40 font-black uppercase tracking-[0.2em] text-[10px]">
                Click background to exit
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TextLesson;